import { Alert, Button, Card, Flex, Skeleton, Space, Table, Tag, Typography } from 'antd';
import React from 'react';

import { useAssessmentList, type AssessmentRowViewModel } from '@/hooks/useAssessmentList';
import { AddIcon } from '@/icons';

/** Props for the `FileCabinetPage` assessment list view. */
export interface FileCabinetPageProps {
  clientId: string;
  projectId: string;
  accessToken: string;
  onOpenStep1: (assessmentInstanceId: string) => void;
  onOpenWorkspace: () => void;
  /** When true, show **Start a New Assessment** (All Assessments route only). */
  showStartNewAssessment?: boolean;
  onStartNewAssessment?: () => void;
}

const { Text } = Typography;

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'default' },
  in_progress: { label: 'In Progress', color: 'processing' },
  in_review: { label: 'In Review', color: 'warning' },
  complete: { label: 'Complete', color: 'success' },
};
const DEFAULT_BADGE = { label: 'Unknown', color: 'default' };

/** Tabular list of project assessments with status badges and navigation to resume or start them. */
export const FileCabinetPage: React.FC<FileCabinetPageProps> = ({
  clientId,
  projectId,
  accessToken,
  onOpenStep1,
  onOpenWorkspace,
  showStartNewAssessment = false,
  onStartNewAssessment,
}) => {
  const { items, loading, error } = useAssessmentList(accessToken, clientId, projectId);

  if (loading) {
    return (
      <Card>
        <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  if (error !== null && error !== undefined) {
    return (
      <Card>
        <Alert type="error" message={error} showIcon />
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">No assessments yet. Start one to see it listed here.</Text>
          {showStartNewAssessment && onStartNewAssessment ? (
            <div>
              <Button type="primary" icon={<AddIcon />} onClick={onStartNewAssessment}>
                Start a New Assessment
              </Button>
            </div>
          ) : null}
        </Space>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex justify="space-between" align="center" gap="small" wrap="wrap">
          <Text type="secondary">Browse assessments and continue where you left off.</Text>
          {showStartNewAssessment && onStartNewAssessment ? (
            <Button type="primary" onClick={onStartNewAssessment}>
              Start a New Assessment
            </Button>
          ) : null}
        </Flex>
        <Table<AssessmentRowViewModel>
          rowKey="assessmentId"
          dataSource={items}
          pagination={false}
          columns={[
            {
              title: 'Assessment',
              dataIndex: 'title',
              key: 'title',
              ellipsis: true,
            },
            {
              title: 'Status',
              key: 'status',
              width: 140,
              render: (_value, record) => {
                const badge = STATUS_BADGE[record.status] ?? DEFAULT_BADGE;
                return <Tag color={badge.color}>{badge.label}</Tag>;
              },
            },
            {
              title: 'Progress',
              key: 'progress',
              width: 160,
              render: (_value, record) => (
                <Text type="secondary">
                  Step {record.currentStep}/{record.totalSteps}
                </Text>
              ),
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 280,
              render: (_value, record) => (
                <Space>
                  <Button type="primary" onClick={() => onOpenStep1(record.assessmentId)}>
                    Open Step 01
                  </Button>
                  <Button onClick={onOpenWorkspace}>Workspace</Button>
                </Space>
              ),
            },
          ]}
        />
      </Space>
    </Card>
  );
};

export default FileCabinetPage;
